import React, { useState, useCallback } from 'react';
import { Code } from 'lucide-react';
import ErrorBoundary from '../shared/ErrorBoundary';
import Accordion from '../shared/Accordion';
import InputTab from './InputTab';
import OutputTab from './OutputTab';
import SecurityTab from './SecurityTab';
import ConfigurationTab from './ConfigurationTab';
import { palette, withOpacity } from '../../../../utils/designTokens';

/**
 * RawToggleButton - Small </> icon to toggle between formatted and raw view
 */
const RawToggleButton = ({ active, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    className="p-1 rounded transition-colors"
    style={{
      backgroundColor: active ? withOpacity(palette.accents.primary.base, 19) : 'transparent',
      color: active ? palette.accents.primary.base : palette.text.muted,
    }}
    title={active ? 'Show formatted' : 'Show raw'}
  >
    <Code size={14} />
  </button>
);

/**
 * useRawToggle - Hook to manage raw toggle state and provide button + content switching
 */
const useRawToggle = (formattedContent, rawContent) => {
  const [showRaw, setShowRaw] = useState(false);
  const toggle = useCallback(() => setShowRaw(prev => !prev), []);

  const hasToggle = formattedContent && rawContent;
  const headerActions = hasToggle ? <RawToggleButton active={showRaw} onClick={toggle} /> : null;
  const content = hasToggle && showRaw ? rawContent : formattedContent;

  return { headerActions, content };
};

/**
 * OutlineTabInner - Inner component that can use hooks
 */
/**
 * Check if span has a distinct raw input view (different from formatted).
 * Only true when formatted messages exist AND there's underlying raw data to show.
 */
const hasDistinctRawInput = (span, spanKind) => {
  // Tool spans: show raw toggle when input_value exists (formatted shows card, raw shows JSON)
  if (spanKind === 'tool') return !!span.input_value;
  if (spanKind !== 'llm') return false;
  // LLM spans: raw toggle makes sense when we have structured messages
  // AND underlying raw data (input_value or raw input_messages JSON)
  try {
    const attrs = typeof span.attributes === 'string' ? JSON.parse(span.attributes) : span.attributes || {};
    const llmAttrs = attrs?.llm || {};
    if (llmAttrs.input_messages) return true;
    if (Object.keys(attrs).some(key => key.startsWith('llm.input_messages.'))) return true;
  } catch { /* ignore */ }
  return !!span.input_value;
};

/**
 * Check if span has a distinct raw output view (different from formatted).
 * Only true for LLM spans where we show MessageList formatted but have raw JSON underneath.
 */
const hasDistinctRawOutput = (span, spanKind) => {
  // Tool spans: show raw toggle when output_value exists
  if (spanKind === 'tool') return !!span.output_value;
  if (spanKind !== 'llm') return false;
  try {
    const attrs = typeof span.attributes === 'string' ? JSON.parse(span.attributes) : span.attributes || {};
    const llmAttrs = attrs?.llm || {};
    if (llmAttrs.output_messages) return true;
  } catch { /* ignore */ }
  return !!span.output_value;
};

const OutlineTabInner = ({ span, spans, spanKind, options, drilldownApi, drilldownMessages, drilldownMessagesLoading, drilldownMessagesError, fetchDrilldownMessages }) => {
  const { hasInput, hasOutput, hasConfiguration, hasSecurity } = options;

  const hasRawInput = hasInput && hasDistinctRawInput(span, spanKind);
  const hasRawOutput = hasOutput && hasDistinctRawOutput(span, spanKind);
  const hasErrorDetection = Array.isArray(span.detections)
    ? span.detections.some(detection => detection?.classifier_key === 'error_detection' && (detection?.status === 'detected' || detection?.detected))
    : false;
  const hasErrorOutput = span.status_code === 'ERROR' || span.metrics?.has_error || hasErrorDetection;

  // Input raw toggle
  const inputToggle = useRawToggle(
    hasInput ? <InputTab span={span} spans={spans} spanKind={spanKind} drilldownApi={drilldownApi} drilldownMessages={drilldownMessages} drilldownMessagesLoading={drilldownMessagesLoading} drilldownMessagesError={drilldownMessagesError} fetchDrilldownMessages={fetchDrilldownMessages} /> : null,
    hasRawInput ? <InputTab span={span} spans={spans} spanKind={spanKind} showRaw drilldownApi={drilldownApi} drilldownMessages={drilldownMessages} drilldownMessagesLoading={drilldownMessagesLoading} drilldownMessagesError={drilldownMessagesError} fetchDrilldownMessages={fetchDrilldownMessages} /> : null,
  );

  // Output raw toggle
  const outputToggle = useRawToggle(
    hasOutput ? <OutputTab span={span} spanKind={spanKind} drilldownApi={drilldownApi} drilldownMessages={drilldownMessages} drilldownMessagesLoading={drilldownMessagesLoading} drilldownMessagesError={drilldownMessagesError} fetchDrilldownMessages={fetchDrilldownMessages} hideErrorHeader suppressErrorOutput={hasErrorOutput} /> : null,
    hasRawOutput ? <OutputTab span={span} spanKind={spanKind} showRaw drilldownApi={drilldownApi} drilldownMessages={drilldownMessages} drilldownMessagesLoading={drilldownMessagesLoading} drilldownMessagesError={drilldownMessagesError} fetchDrilldownMessages={fetchDrilldownMessages} /> : null,
  );
  const errorContent = hasErrorOutput
    ? <OutputTab span={span} spanKind={spanKind} drilldownApi={drilldownApi} drilldownMessages={drilldownMessages} drilldownMessagesLoading={drilldownMessagesLoading} drilldownMessagesError={drilldownMessagesError} fetchDrilldownMessages={fetchDrilldownMessages} hideErrorHeader forceErrorOutput />
    : null;

  const sections = [];

  if (hasSecurity) {
    sections.push({
      title: 'Signals',
      content: <SecurityTab span={span} />,
      defaultExpanded: true,
    });
  }

  if (hasErrorOutput) {
    sections.push({
      title: 'Error',
      content: errorContent,
      defaultExpanded: true,
    });
  }

  if (hasInput) {
    sections.push({
      title: 'Input',
      content: inputToggle.content,
      headerActions: inputToggle.headerActions,
      defaultExpanded: true,
    });
  }

  if (hasOutput) {
    sections.push({
      title: 'Output',
      content: outputToggle.content,
      headerActions: outputToggle.headerActions,
      defaultExpanded: true,
    });
  }

  if (hasConfiguration) {
    sections.push({
      title: 'Configuration',
      content: <ConfigurationTab span={span} spans={spans} spanKind={spanKind} drilldownApi={drilldownApi} />,
      defaultExpanded: false,
    });
  }

  if (sections.length === 0) {
    return (
      <div className="p-4 text-sm text-text-muted">
        No outline data available for this span.
      </div>
    );
  }

  return <Accordion sections={sections} />;
};

/**
 * OutlineTab - Combined view of Input, Configuration (collapsed), Output, and Signals inline.
 */
const OutlineTab = ({ span, spans = [], spanKind, options = {}, drilldownApi = null, drilldownMessages = null, drilldownMessagesLoading = false, drilldownMessagesError = null, fetchDrilldownMessages = null }) => {
  if (!span) {
    return (
      <div className="p-4 text-sm text-text-muted">
        No span data available.
      </div>
    );
  }

  return (
    <ErrorBoundary>
      <OutlineTabInner span={span} spans={spans} spanKind={spanKind} options={options} drilldownApi={drilldownApi} drilldownMessages={drilldownMessages} drilldownMessagesLoading={drilldownMessagesLoading} drilldownMessagesError={drilldownMessagesError} fetchDrilldownMessages={fetchDrilldownMessages} />
    </ErrorBoundary>
  );
};

export default OutlineTab;
